import React from 'react';
import { useNavigate } from 'react-router-dom';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';
import { Language } from '@/types';
import { ArrowRight, Terminal, Database, Network } from 'lucide-react';
import RasterGridBackdrop from '@/components/ui/RasterGridBackdrop';

interface GeoAgentPageProps {
  lang: Language;
}

const GeoAgentPage: React.FC<GeoAgentPageProps> = ({ lang }) => {
  const isAr = lang === 'ar';
  const navigate = useNavigate();

  const capabilities = [
    {
      icon: <Terminal className="text-[#0E93A3]" size={20} />,
      title: isAr ? 'أتمتة أوامر GIS المكانية' : 'Autonomous GIS Command Automation',
      desc: isAr
        ? 'تحويل الأوامر اللفظية إلى نصوص ArcPy و QGIS دقيقة مع فحص طوبولوجي ذاتي.'
        : 'Convert natural language intent into deterministic ArcPy and QGIS scripts with automated topological validation.',
    },
    {
      icon: <Database className="text-[#0E93A3]" size={20} />,
      title: isAr ? 'استعلام واستيعاب طبقات الفضاء' : 'Multi-Modal Spatial Ingestion',
      desc: isAr
        ? 'معالجة آنية لبيانات Sentinel-2 و Landsat و DEM واستخراج مؤشرات الإجهاد الطيفي.'
        : 'Real-time ingestion of Sentinel-2, Landsat, and DEM elevation layers with automated spectral stress extraction.',
    },
    {
      icon: <Network className="text-[#0E93A3]" size={20} />,
      title: isAr ? 'تنسيق الوكلاء المتعددين' : 'Multi-Agent Spatial Workflows',
      desc: isAr
        ? 'توزيع مهام التحليل الجغرافي المعقدة على شبكة وكلاء متخصصة في الحساب والهندسة المكانية.'
        : 'Orchestrate multi-step spatial reasoning tasks across distributed agents optimized for computational geometry.',
    },
  ];

  return (
    <PageTransition>
      <SEO 
        title={isAr ? 'منظومة الوكلاء المكانية - VEXL GeoAgent' : 'VEXL GeoAgent - Spatial Agent Systems'} 
        description={isAr ? 'منظومة وكلاء ذكاء اصطناعي مستقلة لمعالجة وتحليل البيانات المكانية المعقدة' : 'Autonomous spatial intelligence & GIS agent orchestration system.'} 
        lang={lang} 
      />
      <div className={`min-h-screen bg-[#05070A] text-[#EDF0F1] pt-28 pb-24 px-6 relative ${isAr ? 'font-arabic' : 'font-sans'}`} dir={isAr ? 'rtl' : 'ltr'}>
        <RasterGridBackdrop type="type-a" />

        {/* ─── HERO SECTION (Editorial Two-Column Split) ─── */}
        <section className="max-w-[1400px] mx-auto mb-16 border-b border-[#262C31] pb-16 relative z-10">
          <div
            className={`
              w-full grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center
              ${isAr ? 'text-right' : 'text-left'}
            `}
          >
            {/* Left Column (Headline, Copy, CTAs) */}
            <div className="lg:col-span-7 space-y-6">
              {/* Eyebrow Badge */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#12161A] border border-[#262C31] text-[#0E93A3] text-xs font-mono tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0E93A3] animate-pulse" />
                <span>{isAr ? 'منظومة الوكلاء المكانية المستقلة' : 'Autonomous Spatial Agent Network'}</span>
              </div>

              <h1
                className="text-3xl md:text-5xl lg:text-[54px] font-medium text-[#EDF0F1] tracking-[-0.5px] max-w-2xl leading-[1.12]"
                style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
              >
                {isAr ? 'منظومة الوكلاء المكانية الذاتية' : 'Autonomous Spatial Intelligence Agents'}
              </h1>

              <p className="text-[#9AA5AC] text-base md:text-[17px] leading-[1.6] max-w-xl font-normal">
                {isAr
                  ? 'منظومة وكلاء ذكاء مكاني متطورة لأتمتة سير عمل نظم المعلومات الجغرافية، وتحليل أطياف الأقمار الصناعية، وحل المسائل المكانية المعقدة بموثوقية مؤسسية.'
                  : 'Advanced geospatial agent network automating multi-step GIS pipelines, multi-spectral satellite reasoning, and planetary data engineering with zero-trust verification.'}
              </p>

              <div className="flex items-center gap-3 pt-2 flex-wrap">
                <button
                  onClick={() => navigate('/contact')}
                  className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0E93A3] hover:bg-[#075C68] text-white font-medium text-xs font-mono uppercase tracking-wider shadow-[0_0_15px_rgba(14,147,163,0.3)] transition-all duration-200"
                >
                  <span>{isAr ? 'طلب وصول للمعاينة الخاصة' : 'Request Private Preview'}</span>
                  <ArrowRight size={13} className={isAr ? "rotate-180 group-hover:-translate-x-0.5 transition-transform" : "group-hover:translate-x-0.5 transition-transform"} />
                </button>
              </div>
            </div>

            {/* Right Column (Visual: geo.webp) */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-[#262C31] bg-[#0E1217] overflow-hidden shadow-2xl">
                {/* Modern Window Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-[#12161A] border-b border-[#262C31] text-xs font-mono">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#262C31]" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#262C31]" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#262C31]" />
                    <span className="text-[#9AA5AC] pl-2 border-l border-[#262C31] text-[11px]">
                      GeoAgent · Autonomous Spatial Engine
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-[#0E93A3] text-[11px]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0E93A3] animate-pulse" />
                    <span>Agent Active</span>
                  </div>
                </div>

                {/* Raster Visual Frame */}
                <div className="relative aspect-[4/3] bg-[#05070A] overflow-hidden">
                  <img
                    src="/images/products/geo.webp"
                    alt="VEXL GeoAgent Autonomous Geospatial Network"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── CAPABILITIES SECTION ─── */}
        <section className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {capabilities.map((cap, i) => (
              <div
                key={i}
                className="p-8 rounded-xl bg-[#12161A] border border-[#262C31] hover:border-[#0E93A3]/60 transition-all duration-200 space-y-4"
              >
                <div className="w-10 h-10 rounded-lg bg-[#171C21] border border-[#262C31] flex items-center justify-center">
                  {cap.icon}
                </div>
                <h3 className="text-lg font-medium text-[#EDF0F1] tracking-tight">
                  {cap.title}
                </h3>
                <p className="text-[#9AA5AC] text-sm leading-[1.6] font-normal">
                  {cap.desc}
                </p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default GeoAgentPage;

